import { useMemo } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';

import {
  surveyHelperContext,
  reviewHelperContext,
} from '@/patient-app-common/helpers/survey_helper';
import {
  useDeleteTrackerDraftMutation,
  useSaveTrackerDraftMutation,
} from '@/patient-app-common/api/trackerApi';
import { useGetPatientQuery } from '@/patient-app-common/api/patientApi';

export default function useSurveyDrafts() {
  const { data: patient } = useGetPatientQuery();

  const [updateDraft] = useSaveTrackerDraftMutation();
  const [deleteDraft] = useDeleteTrackerDraftMutation();

  let updateDraftController;

  const surveys = useMemo(() => {
    if (!patient) return [];
    return patient.surveys;
  }, [patient]);

  const surveyDrafts = useMemo(() => {
    if (!patient) return [];
    return patient.surveyDrafts;
  }, [patient]);

  const expireDraft = (surveyData, events = []) => {
    const { draft } = surveyData;
    const draftDate = draft.draftCreatedAt;

    const isHealthCheck = surveyData.mainTracker;
    const eventId = draft.applicationEventId;

    let expired = false;

    if (isHealthCheck) {
      const isToday = dayjs()
        .startOf('day')
        .isSame(dayjs(draftDate).startOf('day'));

      expired = !isToday;
    }

    if (eventId && events?.length) {
      const appEvent = _.find(events, ['id', eventId.toString()]);

      if (appEvent?.startDateTime) {
        const isCurrent = dayjs(draftDate)
          .startOf('day')
          .isSameOrAfter(dayjs(appEvent.startDateTime).startOf('day'));

        expired = !isCurrent;
      }
    }

    if (expired) {
      try {
        deleteDraft({ procedureId: surveyData.id });
      } catch (e) {
        console.log(e);
      }
    }

    return expired;
  };

  const dataUrlToFile = async (imageURL) => {
    const response = await fetch(imageURL, { redirect: 'error' });

    if (!response.ok) {
      throw new Error(`Draft image fetch failed: ${response.status}`);
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.startsWith('image/')) {
      throw new Error(
        `Draft image is not an image (content-type: ${contentType})`
      );
    }

    const blob = await response.blob();
    return new File([blob], 'file.jpg', { type: contentType });
  };

  const setAnswersWithFiles = async (imageKeys, draft) => {
    const cameraAnswers = {};

    await Promise.all(
      imageKeys.map(async (key) => {
        const answerKey = key.split('_')[1];
        const url = draft[answerKey];
        try {
          cameraAnswers[answerKey] = await dataUrlToFile(url);
        } catch (e) {
          console.error(`Failed to load draft image for ${answerKey}:`, e);
        }
      })
    );
    return cameraAnswers;
  };

  const parseDraftAnswers = async (draft) => {
    const source = JSON.parse(draft.source);
    let draftAnswers = { ...source.answers };

    if (draft.imageKeys) {
      const imageKeys = JSON.parse(draft.imageKeys);

      const cameraAnswers = await setAnswersWithFiles(imageKeys, draft);
      draftAnswers = { ...source.answers, ...cameraAnswers };
    }

    return {
      draftAnswers,
      draftIndex: source.index,
    };
  };

  const parseDraftData = async (surveyData, patientDatesInMS, profile) => {
    const helper = surveyHelperContext({
      patientDatesInMS,
      surveyData,
      editMode: true,
    });

    const { draft } = surveyData;
    const { draftAnswers: currentAnswers, draftIndex } =
      await parseDraftAnswers(draft);

    const { validQuestions, validProfiles, validAnswers } =
      helper.validateQuestions({
        currentIndex: 0,
        currentAnswers,
        currentHistory: [0],
        currentProfiles: [profile],
      });

    let draftHistory = validQuestions;
    let draftProfileHistory = validProfiles;
    if (draftIndex) {
      draftHistory = _.slice(validQuestions, 0, draftIndex);
      draftProfileHistory = _.slice(validProfiles, 0, draftIndex);
    }

    const draftData = {
      draftCreatedAt: draft.draftCreatedAt,
      draftAnswers: validAnswers,
      draftHistory,
      draftProfiles: draftProfileHistory,
    };

    return draftData;
  };

  const saveDraft = async ({ answers, eventId, surveyData, index }) => {
    let params = {
      procedureId: surveyData.id,
      applicationEventId: eventId,
      source: JSON.stringify({ answers, index }),
    };

    const photoParams = reviewHelperContext({
      surveyData,
      answers,
    }).createPhotoParams();

    if (photoParams && Object.keys(photoParams).length > 0) {
      params = { ...params, ...photoParams };
    }

    if (updateDraftController) updateDraftController.abort();
    updateDraftController = new AbortController();

    try {
      const result = await updateDraft(params, {
        signal: updateDraftController.signal,
      }).unwrap();
      return result;
    } catch (e) {
      console.error(e);
    }
  };

  return {
    surveys,
    surveyDrafts,
    expireDraft,
    saveDraft,
    parseDraftData,
  };
}
